import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { AnimatePresence, m } from 'framer-motion';
import { ArrowUp, MessageCircle, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useSupportChat from '../features/support/useSupportChat';
import { Alert, Button, Field, Input, Spinner, Textarea, cx } from './ui';

const HIDDEN_ON = ['/register', '/sign-in', '/forgot-password', '/welcome', '/payment', '/admin'];
const time = (ts) => {
  const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
  if (!d) return '';
  const today = new Date().toDateString() === d.toDateString();
  return d.toLocaleString(undefined, today ? { hour: '2-digit', minute: '2-digit' } : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

function Bubble({ msg }) {
  const mine = msg.from === 'user';
  return (
    <div className={cx('flex flex-col', mine ? 'items-end' : 'items-start')}>
      {!mine && <span className="mb-1 px-1 text-[11px] font-medium text-ink-muted">{msg.author_name ? `${msg.author_name} · Plastic Analytics` : 'Plastic Analytics'}</span>}
      <div className={cx('max-w-[85%] whitespace-pre-wrap break-words rounded-3xl px-4 py-2.5 text-sm leading-relaxed',
        mine ? 'rounded-br-lg bg-primary text-on-primary' : 'rounded-bl-lg bg-paper-sunk text-ink', msg.pending && 'opacity-70')}>
        {msg.text}
      </div>
      <span className="mt-1 px-1 text-[11px] text-ink-faint">
        {msg.failed ? <span className="text-signal-bad">Not sent — {msg.failed}</span> : msg.pending ? 'Sending…' : time(msg.created_at)}
      </span>
    </div>
  );
}

function StartForm({ onStart }) {
  const { user, isMember } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', message: '', website: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (isMember && user) setForm((f) => ({ ...f, name: f.name || user.displayName || '', email: f.email || user.email || '' }));
  }, [isMember, user]);
  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setErrors({ ...errors, [k]: null }); };
  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = 'Add your name so we know who we’re talking to.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) errs.email = 'Enter an email so we can reply if you leave.';
    if (form.message.trim().length < 2) errs.message = 'Write your question.';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true); setErr('');
    try { await onStart({ name: form.name.trim(), email: form.email.trim(), message: form.message.trim(), website: form.website }); }
    catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      {err && <Alert tone="bad">{err}</Alert>}
      <Field label="Your question" error={errors.message}><Textarea rows={3} value={form.message} onChange={set('message')} placeholder="e.g. Does it read Renishaw .wdf maps?" autoFocus /></Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" error={errors.name}><Input value={form.name} onChange={set('name')} autoComplete="name" /></Field>
        <Field label="Email" error={errors.email}><Input type="email" value={form.email} onChange={set('email')} autoComplete="email" /></Field>
      </div>
      {/* Invisible to people; bots fill it in. */}
      <input type="text" name="website" value={form.website} onChange={set('website')} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      <Button type="submit" full loading={busy}>Start chat</Button>
      <p className="text-xs leading-relaxed text-ink-muted">
        We use your name, email and messages only to help you, and delete the conversation 24 months after the last message.
        {!isMember && ' To keep the chat on this device we create a temporary anonymous session.'} <Link to="/privacy-policy" className="underline">Privacy policy</Link>
      </p>
    </form>
  );
}

function Composer({ onSend, disabled }) {
  const [text, setText] = useState('');
  const ref = useRef(null);
  const submit = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText('');
    try { await onSend(t); } catch { /* shown on the message */ }
    ref.current?.focus();
  };
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 140)}px`; }
  }, [text]);
  return (
    <form onSubmit={submit} className="flex items-end gap-2 border-t border-line bg-surface p-3">
      <label htmlFor="chat-input" className="sr-only">Message</label>
      <textarea id="chat-input" ref={ref} rows={1} value={text} disabled={disabled} maxLength={4000}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
        placeholder="Write a message…"
        className="max-h-36 min-h-[2.75rem] flex-1 resize-none rounded-3xl border-0 bg-paper-sunk px-4 py-3 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-accent" />
      <button type="submit" disabled={!text.trim() || disabled} aria-label="Send"
        className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-primary text-on-primary transition active:scale-95 disabled:opacity-40">
        <ArrowUp className="h-5 w-5" />
      </button>
    </form>
  );
}

export default function SupportWidget() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(() => params.get('chat') === 'open');
  const chat = useSupportChat(open);
  const listRef = useRef(null);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('pa:open-chat', onOpen);
    return () => window.removeEventListener('pa:open-chat', onOpen);
  }, []);
  useEffect(() => { if (params.get('chat') === 'open') setOpen(true); }, [params]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }); }, [chat.messages.length, open]);

  if (HIDDEN_ON.some((p) => pathname.startsWith(p))) return null;
  const hasThread = !!chat.threadId;

  return (
    <div className="fixed bottom-4 right-4 z-[80] sm:bottom-5 sm:right-5">
      <AnimatePresence>
        {open && (
          <m.div role="dialog" aria-label="Chat with the team"
            initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={{ type: 'spring', bounce: 0, duration: 0.3 }} style={{ transformOrigin: 'bottom right' }}
            className="absolute bottom-16 right-0 flex max-h-[min(40rem,calc(100dvh-6rem))] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-4xl bg-surface shadow-float ring-1 ring-line">
            <div className="flex-none bg-night px-6 py-5 text-white">
              <p className="font-semibold">Chat with the team</p>
              <p className="mt-1 text-sm text-white/70">Questions about the software, pricing or a result? A real person replies here — and by email if you’ve gone.</p>
            </div>
            {hasThread ? (
              <>
                <div ref={listRef} className="min-h-[12rem] flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-5" aria-live="polite">
                  {chat.messages.length === 0 && <div className="flex justify-center py-8"><Spinner className="h-5 w-5 text-ink-muted" /></div>}
                  {chat.messages.map((msg) => <Bubble key={msg.id} msg={msg} />)}
                  {chat.thread?.status === 'closed' && <p className="text-center text-xs text-ink-muted">This conversation was closed. Send a message to pick it up again.</p>}
                  {chat.thread?.last_from === 'user' && chat.messages.length > 0 && (
                    <p className="text-center text-xs text-ink-faint">We’ll reply here and email {chat.thread?.email ? <strong className="font-medium">{chat.thread.email}</strong> : 'you'} if you’ve left.</p>
                  )}
                </div>
                <Composer onSend={chat.send} />
              </>
            ) : (
              <div className="overflow-y-auto px-6 py-5">
                {chat.loading ? <div className="flex justify-center py-8"><Spinner className="h-5 w-5 text-ink-muted" /></div> : <StartForm onStart={chat.start} />}
              </div>
            )}
          </m.div>
        )}
      </AnimatePresence>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        aria-label={open ? 'Close chat' : chat.unread ? `Chat with the team — ${chat.unread} new ${chat.unread === 1 ? 'reply' : 'replies'}` : 'Chat with the team'}
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary text-on-primary shadow-float transition active:scale-95">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!open && chat.unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-signal-bad px-1 text-[11px] font-semibold text-white ring-2 ring-paper">{chat.unread}</span>
        )}
      </button>
    </div>
  );
}
